import type { ShotOptionen, ShotResult } from './screenshot';
import { abzugsOptionen } from './screenshot';

/**
 * Der Bogenabzug im Browser — über `getDisplayMedia`.
 *
 * ⚠️ **Der Abzug entsteht nicht einmal, sondern mehrfach.** `features/bogen/bogenShot.ts`
 * scrollt den Bogen durch und zieht Kachel für Kachel (`part`). Würde je Kachel neu gefragt,
 * stünde der Prüfer vor einem halben Dutzend Freigabedialoge. Der Strom wird deshalb
 * **gehalten** und erst nach einer Ruhezeit wieder losgelassen.
 *
 * ⚠️ **Die Nutzergeste ist der Absendeknopf.** Chrome verlangt für `getDisplayMedia` eine
 * frische Bedienung; der Abzug hängt am Tipp auf „Absenden" und liegt damit innerhalb des
 * Zeitfensters. Ohne diese Kette gäbe es kein Bild — das ist keine Schwäche des Rückfalls,
 * sondern die Regel des Browsers.
 *
 * ⚠️ **Was auf dem Abzug landet, entscheidet der Prüfer im Freigabedialog.** Wählt er „Dieser
 * Tab", ist es genau die Seite; wählt er „Fenster", ist die Adressleiste mit darauf. Nativ
 * nimmt `WKWebView.takeSnapshot` immer genau die App. Versprechen lässt sich das hier nicht.
 */

export interface SchirmTeile {
  /** Fragt den Browser nach dem Strom — einmal je Ruhezeit. */
  hollStrom: () => Promise<MediaStream>;
  /** Zieht ein Einzelbild aus dem Strom. */
  zeichne: (strom: MediaStream, optionen: Required<ShotOptionen>) => Promise<ShotResult>;
  /** Wie lange der Strom nach dem letzten Abzug noch offen bleibt. */
  leerlaufMs: number;
}

/**
 * Läuft der Strom noch, oder hat der Prüfer die Freigabe inzwischen beendet?
 *
 * Als Typwächter geschrieben, damit der Aufrufer danach ohne Fallunterscheidung weiterkommt.
 */
export function stromLebt(strom: MediaStream | null): strom is MediaStream {
  if (!strom) return false;
  return strom.getTracks().some((spur) => spur.readyState === 'live');
}

export function halteAn(strom: MediaStream | null): void {
  for (const spur of strom?.getTracks() ?? []) spur.stop();
}

/**
 * Hält den Strom über mehrere Abzüge hinweg.
 *
 * Herausgelöst, damit sich das Halten, das Loslassen und die Rückkehr nach einer beendeten
 * Freigabe ohne Browser prüfen lassen.
 */
export class Schirmhalter {
  private strom: MediaStream | null = null;
  private wecker: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly teile: SchirmTeile) {}

  async abzug(optionen: Required<ShotOptionen> = abzugsOptionen()): Promise<ShotResult> {
    // ⚠️ **Auch ein gehaltener Strom kann tot sein**: der Prüfer kann die Freigabe jederzeit
    // über die Leiste des Browsers beenden. Dann wird neu gefragt, statt ein schwarzes Bild
    // zu liefern — ein leerer Bogenabzug fiele erst beim Öffnen der Datei auf.
    let strom = this.strom;
    if (!stromLebt(strom)) {
      halteAn(strom);
      strom = await this.teile.hollStrom();
      this.strom = strom;
    }

    const bild = await this.teile.zeichne(strom, optionen);
    this.planeLoslassen();
    return bild;
  }

  /** Steht eine lebende Freigabe? Dann geht ein Abzug ohne Rückfrage. */
  hatFreigabe(): boolean {
    return stromLebt(this.strom);
  }

  /** Sofort loslassen — für den Fall, dass jemand das Ende kennt. */
  loslassen(): void {
    if (this.wecker !== null) clearTimeout(this.wecker);
    this.wecker = null;
    halteAn(this.strom);
    this.strom = null;
  }

  private planeLoslassen(): void {
    if (this.wecker !== null) clearTimeout(this.wecker);
    this.wecker = setTimeout(() => {
      this.wecker = null;
      halteAn(this.strom);
      this.strom = null;
    }, this.teile.leerlaufMs);
  }
}

/* ------------------------------------------------------------------ Die echten Teile */

async function browserStrom(): Promise<MediaStream> {
  // `preferCurrentTab` kennt nur Chrome/Edge — genau die sind der Zielbrowser
  // (`docs/plan-web-rueckfaelle.md`). Anderswo fällt die Angabe still weg.
  return navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: false,
    preferCurrentTab: true,
  } as DisplayMediaStreamOptions);
}

/**
 * Wie gross die Leinwand wird, auf die der Strom gezeichnet wird.
 *
 * ⚠️ **Gemessen wird an der Seitenbreite, nicht am Pixelverhältnis.** „1x" meint die
 * Punktgrösse der Seite (`innerWidth`). Bis 2026-11-01 stand hier „durch `devicePixelRatio`
 * teilen" — unter der Annahme, der Strom komme in Geräte-Pixeln. Chrome lieferte den Tab aber
 * in CSS-Pixeln (1024 bei DPR 2), und der Abzug schrumpfte auf unlesbare 512 px.
 *
 * ⚠️ **Nie vergrössern.** Aus einem 1x-Strom ein 2x-Bild zu rechnen erfände Pixel, die
 * niemand gesehen hat — auf einem Beweisdokument genau das Falsche, und grösser wird die
 * Datei obendrein.
 *
 * ⚠️ **Die Höhe folgt der gerundeten Breite.** Beide für sich zu runden verschöbe das
 * Seitenverhältnis um bis zu einem Pixel, und der Bogen stünde leicht verzerrt auf dem Beleg.
 */
export function zielMasse(
  videoBreite: number,
  videoHoehe: number,
  seitenBreite: number,
  skalierung: number,
): { breite: number; hoehe: number } {
  // In Testumgebungen ist `innerWidth` gelegentlich 0 oder NaN; dann bleibt der Strom ganz,
  // statt eine Leinwand der Grösse 0 zu ergeben — ein leeres Beweisbild.
  const faktor = Number.isFinite(seitenBreite) && seitenBreite > 0
    ? Math.min(1, (seitenBreite * skalierung) / videoBreite)
    : 1;
  const breite = Math.max(1, Math.round(videoBreite * faktor));
  const hoehe = Math.max(1, Math.round(videoHoehe * (breite / videoBreite)));
  return { breite, hoehe };
}

async function browserBild(
  strom: MediaStream,
  optionen: Required<ShotOptionen>,
): Promise<ShotResult> {
  const el = document.createElement('video');
  el.muted = true;
  el.playsInline = true;
  el.srcObject = strom;

  await new Promise<void>((fertig, scheitert) => {
    el.onloadedmetadata = () => fertig();
    el.onerror = () => scheitert(new Error('Der Bildschirmstrom liess sich nicht lesen.'));
  });
  await el.play().catch(() => undefined);

  if (el.videoWidth === 0) {
    el.srcObject = null;
    throw new Error('Der Bildschirmstrom lieferte kein Bild.');
  }

  const { breite, hoehe } = zielMasse(
    el.videoWidth,
    el.videoHeight,
    window.innerWidth,
    optionen.skalierung,
  );
  const leinwand = document.createElement('canvas');
  leinwand.width = breite;
  leinwand.height = hoehe;
  const stift = leinwand.getContext('2d');
  if (!stift) {
    el.srcObject = null;
    throw new Error('Der Bildschirmstrom lieferte kein Bild.');
  }
  // ⚠️ **Ohne das hochwertige Glätten wird beim Herunterrechnen aus dünner Schrift Griesel**
  // — und den zementiert JPEG danach. Der Vorgabewert ist browserabhängig und oft „low".
  stift.imageSmoothingEnabled = true;
  stift.imageSmoothingQuality = 'high';
  stift.drawImage(el, 0, 0, breite, hoehe);

  // Ohne den `data:`-Vorsatz — `saveBase64` erwartet nur die Nutzlast.
  const base64 = leinwand.toDataURL('image/jpeg', optionen.qualitaet).split(',')[1] ?? '';
  el.srcObject = null;
  return { base64, width: breite, height: hoehe, format: 'jpg' };
}

export function baueSchirmTeile(ueber: Partial<SchirmTeile> = {}): SchirmTeile {
  return {
    hollStrom: ueber.hollStrom ?? browserStrom,
    zeichne: ueber.zeichne ?? browserBild,
    // Lang genug für den ganzen Durchlauf über einen Bogen, kurz genug, dass die
    // Freigabeleiste des Browsers nicht stehen bleibt.
    leerlaufMs: ueber.leerlaufMs ?? 5000,
  };
}
